// Loop-breaker for a chat agent's `afterToolCall` hook.
//
// Tracks the last tool call -- name AND arguments -- and a
// consecutive-repeat count. Once the SAME call (same tool, same args)
// is issued `limit` times in a row it returns `{ terminate: true }` to
// end the run so the user regains control. Keying on the arguments is
// what distinguishes a genuine stuck loop (the model re-issuing the
// identical query forever) from legitimate exploration (the same tool
// with different specs), which must never be cut off: a kernel-builder
// run routinely calls `analytics_query` several times in a row with
// different specs. Stateful -- create one per agent instance.

// Order-independent stringify so argument-key jitter between otherwise
// identical calls doesn't defeat the equality check. Tool args are
// validated JSON, so this can't hit a cycle; the try/catch is a
// belt-and-suspenders guard so the hook never throws inside the loop.
function stableStringify(value) {
    if (value === undefined) return "";
    try {
        return JSON.stringify(value, (_key, v) =>
            v && typeof v === "object" && !Array.isArray(v)
                ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, v[k]]))
                : v,
        );
    } catch {
        return String(value);
    }
}

// Opens the result text of the call the breaker stopped, so a transcript can
// tell this stop from a tool that ends the run on purpose.
export const LOOP_BREAKER_TAG = "[loop-breaker]";

export function makeLoopBreaker({ limit = 3 } = {}) {
    let lastKey = null;
    let count = 0;
    return async (ctx) => {
        const name = ctx?.toolCall?.name || null;
        if (!name) { lastKey = null; count = 0; return undefined; }
        // pi-agent-core surfaces validated tool arguments at ctx.args
        // (AfterToolCallContext). Fall back to ctx.toolCall.arguments so a
        // future framework shape change degrades to a still-correct read
        // rather than silently reverting the guard to name-only matching.
        const args = ctx?.args ?? ctx?.toolCall?.arguments;
        const key = `${name} ${stableStringify(args)}`;
        if (key === lastKey) count += 1;
        else { lastKey = key; count = 1; }
        if (count >= limit) {
            count = 0;
            lastKey = null;
            return {
                content: [{
                    type: "text",
                    text: `${LOOP_BREAKER_TAG} You called "${name}" ${limit} times in a row with identical arguments. Stopping this run. The state is unchanged; re-read your earlier tool results and take a DIFFERENT action when the user replies.`,
                }],
                terminate: true,
            };
        }
        return undefined;
    };
}
